import { useEffect, useMemo, useState } from "react";
import TopNav from "../components/TopNav";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Users, ClipboardList } from "lucide-react";
import SuggestPanel from "./squad/SuggestPanel";
import PlanModal from "./squad/PlanModal";
import TraineeCard from "./squad/TraineeCard";
import TrackFilter from "./squad/TrackFilter";
import AccessDenied from "./squad/AccessDenied";

export default function SquadList() {
  const { user } = useAuth();
  const isLeader = user?.role === "leader" || user?.role === "org_admin" || user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [suggestFor, setSuggestFor] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);

  useEffect(() => {
    if (!isLeader) return;
    api.get("/squad")
      .then((r) => setRows(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [isLeader]);

  const filtered = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.track === filter)),
    [filter, rows]
  );
  const totals = useMemo(() => ({
    trainees: rows.length,
    certs: rows.reduce((a, b) => a + (b.certs || 0), 0),
  }), [rows]);

  const refresh = () => api.get("/squad").then((r) => setRows(r.data));

  if (!isLeader) {
    return <div className="min-h-screen bg-[var(--bg)]"><TopNav /><AccessDenied role={user?.role} /></div>;
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Users size={20} color="var(--cyan)" strokeWidth={1.5} />
          <div className="overline">// LEADER_CONSOLE / SQUAD_ROSTER</div>
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <h1 className="font-display font-bold text-4xl uppercase" data-testid="squad-title">Squad Console</h1>
          <div className="flex items-center gap-3">
            <div className="font-mono text-xs text-[var(--text-2)] uppercase tracking-widest">
              {totals.trainees} trainees &middot; {totals.certs} certifications
            </div>
            <button
              onClick={() => setPlanOpen(true)}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="open-plan"
            >
              <ClipboardList size={14} /> Plan Next 30 Days
            </button>
          </div>
        </div>

        {err && (
          <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-3 mb-4" data-testid="squad-error">{err}</div>
        )}

        <TrackFilter value={filter} onChange={setFilter} />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <TraineeCard key={r.id} trainee={r} onSuggest={setSuggestFor} />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-[var(--text-2)] font-mono text-sm">No trainees match the current filter.</div>
          )}
        </div>

        {suggestFor && (
          <SuggestPanel
            trainee={suggestFor}
            onClose={() => setSuggestFor(null)}
            onAssigned={() => { setSuggestFor(null); refresh(); }}
          />
        )}
        {planOpen && (
          <PlanModal
            onClose={() => setPlanOpen(false)}
            onApplied={() => refresh()}
          />
        )}
      </main>
    </div>
  );
}
